import {
  fetchApps,
  fetchCostsByStacks,
  fetchDatabases,
  fetchEnvironments,
  fetchStacks,
  formatCurrency,
  getStackType,
  selectAppsByOrgAsList,
  selectDatabasesForTableSearch,
  selectEnvironmentsByOrg,
  selectStacksByOrgAsList,
  selectTotalMonthlyCostByStacks,
} from "@app/deploy";
import { selectOrganizationSelected } from "@app/organizations";
import {
  useSelector as useAppSelector,
  useCompositeLoader,
  useLoader,
} from "@app/react";
import { appsUrl, databaseUrl, environmentsUrl, stacksUrl } from "@app/routes";
import { schema } from "@app/schema";
import type { WebState } from "@app/schema";
import { Box, CostEstimateInfoIcon, StatSkeleton } from "@app/ui/shared";
import { Link } from "react-router-dom";
import {
  HOME_DASH_EVENTS,
  homeDashEvent,
  homeDashOrgPayload,
} from "./tuna-events";

const shouldShowSkeleton = (isLoaded: boolean, count: number) =>
  !isLoaded && count === 0;

export const Overview = () => {
  const org = useAppSelector(selectOrganizationSelected);
  const loader = useCompositeLoader([
    fetchStacks,
    fetchApps,
    fetchDatabases,
    fetchEnvironments,
  ]);
  const stacksLoader = useLoader(fetchStacks);
  const appsLoader = useLoader(fetchApps);
  const databasesLoader = useLoader(fetchDatabases);
  const environmentsLoader = useLoader(fetchEnvironments);
  const costsLoader = useLoader(fetchCostsByStacks);

  const stacks = useAppSelector((state: WebState) =>
    selectStacksByOrgAsList(state),
  );
  const apps = useAppSelector(selectAppsByOrgAsList);
  const databases = useAppSelector((state: WebState) =>
    selectDatabasesForTableSearch(state, {
      search: "",
      sortBy: "savings",
      sortDir: "desc",
    }),
  );
  const environments = useAppSelector(selectEnvironmentsByOrg);
  const costs = useAppSelector(schema.costs.selectTable);
  const dedicatedStacksCount = stacks.filter(
    (stack) => getStackType(stack) === "dedicated",
  ).length;
  const environmentsCount = Object.keys(environments).length;
  const appsCount = apps.length;
  const databasesCount = databases.length;
  const isDedicatedStacksTileLoading = shouldShowSkeleton(
    stacksLoader.isSuccess,
    dedicatedStacksCount,
  );
  const isEnvironmentsTileLoading = shouldShowSkeleton(
    environmentsLoader.isSuccess,
    environmentsCount,
  );
  const isAppsTileLoading = shouldShowSkeleton(appsLoader.isSuccess, appsCount);
  const isDatabasesTileLoading = shouldShowSkeleton(
    databasesLoader.isSuccess,
    databasesCount,
  );

  const isCostsTileLoading =
    loader.isInitialLoading ||
    costsLoader.isInitialLoading ||
    !stacks ||
    !costs;
  const totalMonthlyCost = useAppSelector(selectTotalMonthlyCostByStacks);

  return (
    <div>
      <Box size="md" className="divide-x divide-gray-300">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 items-stretch">
          <div className="p-3 flex flex-col items-center justify-between h-full">
            <div className="flex items-start gap-1.5 text-gray-500 mb-2">
              <h3 className="text-sm font-medium text-gray-600">
                Dedicated Stacks
              </h3>
            </div>
            {isDedicatedStacksTileLoading ? (
              <StatSkeleton />
            ) : (
              <Link
                to={stacksUrl()}
                className="text-2xl sm:text-3xl font-semibold text-blue-600 hover:text-blue-800"
                onClick={() =>
                  homeDashEvent(
                    HOME_DASH_EVENTS.OVERVIEW_DEDICATED_STACKS_CLICK,
                    { ...homeDashOrgPayload(org) },
                  )
                }
              >
                {dedicatedStacksCount}
              </Link>
            )}
          </div>

          <div className="p-3 flex flex-col items-center justify-between h-full">
            <div className="flex items-start gap-1.5 text-gray-500 mb-2">
              <h3 className="text-sm font-medium text-gray-600">
                Environments
              </h3>
            </div>
            {isEnvironmentsTileLoading ? (
              <StatSkeleton />
            ) : (
              <Link
                to={environmentsUrl()}
                className="text-2xl sm:text-3xl font-semibold text-blue-600 hover:text-blue-800"
                onClick={() =>
                  homeDashEvent(HOME_DASH_EVENTS.OVERVIEW_ENVIRONMENTS_CLICK, {
                    ...homeDashOrgPayload(org),
                  })
                }
              >
                {environmentsCount}
              </Link>
            )}
          </div>

          <div className="p-3 flex flex-col items-center justify-between h-full">
            <div className="flex items-start gap-1.5 text-gray-500 mb-2">
              <h3 className="text-sm font-medium text-gray-600">Apps</h3>
            </div>
            {isAppsTileLoading ? (
              <StatSkeleton />
            ) : (
              <Link
                to={appsUrl()}
                className="text-2xl sm:text-3xl font-semibold text-blue-600 hover:text-blue-800"
                onClick={() =>
                  homeDashEvent(HOME_DASH_EVENTS.OVERVIEW_APPS_CLICK, {
                    ...homeDashOrgPayload(org),
                  })
                }
              >
                {appsCount}
              </Link>
            )}
          </div>

          <div className="p-3 flex flex-col items-center justify-between h-full">
            <div className="flex items-start gap-1.5 text-gray-500 mb-2">
              <h3 className="text-sm font-medium text-gray-600">
                Managed Databases
              </h3>
            </div>
            {isDatabasesTileLoading ? (
              <StatSkeleton />
            ) : (
              <Link
                to={databaseUrl()}
                className="text-2xl sm:text-3xl font-semibold text-blue-600 hover:text-blue-800"
                onClick={() =>
                  homeDashEvent(HOME_DASH_EVENTS.OVERVIEW_DATABASES_CLICK, {
                    ...homeDashOrgPayload(org),
                  })
                }
              >
                {databasesCount}
              </Link>
            )}
          </div>

          <div className="p-3 flex flex-col items-center justify-between h-full">
            <div className="flex items-start gap-1.5 text-gray-500 mb-2">
              <h3 className="text-sm font-medium text-gray-600">
                Est. Monthly Cost
              </h3>
              <CostEstimateInfoIcon />
            </div>
            {isCostsTileLoading ? (
              <StatSkeleton widthClass="w-24" />
            ) : (
              <div className="text-2xl sm:text-3xl font-semibold">
                {formatCurrency(totalMonthlyCost)}
              </div>
            )}
          </div>
        </div>
      </Box>
    </div>
  );
};
